import { forwardRef } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ' +
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      /*
       * Every variant has an `active:` state beside its `hover:` one. Hover
       * is wrapped in `@media (hover: hover)` by the Tailwind config, so on a
       * phone or an iPad it never fires - and without `active:` a tap would
       * give no feedback at all, on the one device where the finger hides
       * the thing it pressed. The tap highlight is off (globals.css) because
       * these ARE the highlight.
       */
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground active:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground active:bg-accent',
        // Destructive is visually distinct rather than just red text: deleting
        // an invoice should not look like the button next to it.
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
        link: 'text-primary underline-offset-4 hover:underline active:underline',
      },
      /**
       * Taller under a finger, tighter under a mouse.
       *
       * A 36px button is comfortable with a mouse and a poor target for a
       * thumb - Apple asks for 44 and Material for 48. Rather than pick one
       * number and make the desktop feel loose, each size grows on a coarse
       * pointer and keeps its old height on a fine one, so nothing about the
       * laptop layout moves. It used to key on `sm:` - width - which made an
       * iPad a mouse layout operated by a thumb.
       */
      size: {
        // Even "small" is a thumb target on touch. Density is a mouse
        // concern; a 36px button under a finger is a missed tap.
        sm: 'min-h-8 px-3 text-xs touch:min-h-11',
        md: 'min-h-9 px-4 touch:min-h-11',
        lg: 'min-h-10 px-6 touch:min-h-12',
        // `shrink-0`: an icon button beside a truncating name is a flex item,
        // and without it the name's overflow squeezed the button to 43px.
        icon: 'size-9 shrink-0 touch:size-11',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element instead of a <button> - e.g. a router Link. */
  asChild?: boolean;
  loading?: boolean;
}

/**
 * `loading` disables the button as well as showing a spinner.
 *
 * That pairing is the point: a submit button that spins but stays clickable is
 * how one invoice becomes three. Every mutating button in the app passes the
 * mutation's own pending state here.
 *
 * `children` is wrapped in <Slottable> and that is load-bearing, not tidiness.
 * With `asChild`, Radix's Slot merges its props onto its single element child -
 * and the spinner slot next to `children` makes TWO children, so Slot throws
 * "Expected a single React element child" and takes the whole page down with
 * it. Slottable marks which child is the real one, so the spinner can sit
 * beside it. Every `<Button asChild><Link/></Button>` in the app depends on
 * this; without it the dashboard renders a blank screen.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
